import { useCallback, useId, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/index.ts";
import { DataTable, type DataColumn } from "../../auth/DataTable.tsx";
import { OrgSettings } from "./OrgSettings.tsx";
import { LicenseCard } from "./LicenseCard.tsx";
import { Drawer } from "../../auth/Drawer.tsx";
import { Modal } from "../../auth/Modal.tsx";
import { StatusPill } from "../../auth/Pills.tsx";
import { EyeIcon } from "../../auth/RevealInput.tsx";
import { ApiError } from "../../lib/api.ts";
import { membersErrorMessage, useTeamMembers } from "../../lib/use-team-members.ts";
import {
  ACCESS_REQUESTS_KEY,
  approveAccessRequest,
  banMember,
  BANNED_MEMBERS_KEY,
  BanError,
  changeMemberRole,
  denyAccessRequest,
  disableTeamSso,
  getTeamSso,
  listAccessRequests,
  listBannedMembers,
  inviteMember,
  removeMember,
  saveTeamSso,
  testTeamSso,
  TEAM_MEMBERS_KEY,
  TEAM_SSO_KEY,
  unbanMember,
  type BannedMember,
  type MemberRole,
  type AccessRequest,
  type SsoProviderId,
  type TeamMember,
  type TeamSso,
  type TeamSsoTestResult,
} from "../../lib/management-client.ts";
import "./account.css";

/**
 * The TEAM MEMBERS MANAGEMENT screen (controlplane-auth task 13.2; requirements A4). It echoes the
 * AgentDNA member-management surface — `RequestsPage` + `requests/UsersTab` for the list, the
 * `forms/AddUserModal` for invites, and `UserAccessDrawer` + `UserDetailPage` for per-member role
 * changes and removal — in the OATHERA navy/blue theme, driven entirely by the server session.
 *
 * ADMIN-ONLY (A4.1, A5.3): the whole management surface is visible/enabled ONLY when the operator is
 * an `admin` of the CURRENTLY SELECTED team — `useAuth().identity.role === "admin"`. A viewer or
 * operator sees a short "not available for your role" notice and NONE of the table, invite, role-
 * change or remove affordances. This is purely a client-side affordance: the server (via `core`'s
 * RBAC on the forwarded writes) remains the authority and refuses a non-admin write regardless of
 * what the UI shows (see `management-client.ts`).
 *
 * TENANT-SCOPED (Property 5 / A8): the list and every write hit `/api/v1/team/*` with NO tenant id;
 * the BFF scopes them to the session's selected tenant. Switching team (on the profile screen)
 * re-points this screen at the new team's members on the next read.
 *
 * Data uses TanStack Query (the SPA's `createQueryClient`); writes invalidate {@link TEAM_MEMBERS_KEY}
 * so the table re-reads from the server after a successful invite / role change / removal — the UI
 * follows the server, never a locally-mutated list.
 */

const ROLE_OPTIONS: readonly MemberRole[] = ["viewer", "operator", "admin"];

function friendlyError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.kind === "waking") return "The control plane is waking up. Please try again in a moment.";
    if (error.kind === "network") return "We could not reach the control plane. Check your connection.";
    if (error.kind === "forbidden") return "You are not permitted to do that.";
    if (error.kind === "malformed") return "That action is not available yet. Please try again later.";
  }
  return fallback;
}

export function TeamMembersScreen(): ReactNode {
  const auth = useAuth();
  const identity = auth.identity;
  const isAdmin = identity?.role === "admin";

  // ADMIN-ONLY affordance: a non-admin never sees the management surface (server still enforces).
  if (!isAdmin) {
    return (
      <section className="oa-auth oa-account" aria-label="Member Management">
        <header className="oa-account__header">
          <h1 className="oa-account__title">Member Management</h1>
        </header>
        <div className="oa-account__card" data-kind="restricted">
          <h2 className="oa-account__card-title">Not available for your role</h2>
          <p className="oa-account__card-note">
            Only a team admin can manage members. Ask an admin of your team if you need a change.
          </p>
        </div>
      </section>
    );
  }

  return (
    <AdminMembersView
      teamName={identity?.team?.name ?? identity?.team?.id ?? "your team"}
      selfId={identity?.user.id ?? null}
    />
  );
}

/**
 * Map a {@link BanError} guard code to inline copy. The server is the authority on these guards
 * (self-ban, last admin, missing/already-banned, not-banned); the UI only mirrors them so an admin
 * sees *why* a ban/unban was refused without reading a raw code.
 */
function banErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof BanError) {
    switch (error.code) {
      case "cannot_ban_self":
        return "You cannot ban your own membership.";
      case "last_admin":
        return "You cannot ban the last active admin of this team.";
      case "not_bannable":
        return "That member cannot be banned (no active membership, or already banned).";
      case "not_banned":
        return "That member is not banned.";
      default:
        return fallback;
    }
  }
  return friendlyError(error, fallback);
}

/** The admin-only members surface — rendered only once the operator is confirmed admin. */
/** Members shown per page in the members panel; the panel scrolls if a page still overflows it. */
const MEMBERS_PAGE_SIZE = 10;

function AdminMembersView(props: { teamName: string; selfId: string | null }): ReactNode {
  const queryClient = useQueryClient();
  const [memberPage, setMemberPage] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selected, setSelected] = useState<TeamMember | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [banError, setBanError] = useState<string | null>(null);
  const [confirmBan, setConfirmBan] = useState<TeamMember | null>(null);

  // Fail fast (one quick retry, then a clear message) — see `useTeamMembers` / `membersErrorMessage`.
  // The IDP service dashboard reads the same hook, so both surfaces share one query key.
  const membersQuery = useTeamMembers();

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_KEY });
  }, [queryClient]);

  // After a ban the member leaves the directory and appears in the Banned users list, and (because
  // the row was pending/active) the Access Requests list may change too — invalidate all three so
  // every surface re-reads from the server (the UI follows the server, never a local mutation).
  const banMutation = useMutation({
    mutationFn: (userId: string) => banMember(userId),
    onSuccess: async () => {
      setBanError(null);
      setConfirmBan(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_KEY }),
        queryClient.invalidateQueries({ queryKey: BANNED_MEMBERS_KEY }),
        queryClient.invalidateQueries({ queryKey: ACCESS_REQUESTS_KEY }),
      ]);
      setBanner("Member banned.");
    },
    onError: (e) => setBanError(banErrorMessage(e, "Could not ban the member.")),
  });

  const members = membersQuery.data ?? [];
  // Pagination of the members panel. The page index is clamped, so removing or banning the last
  // member of the final page lands on the new last page rather than an empty one.
  const memberPageCount = Math.max(1, Math.ceil(members.length / MEMBERS_PAGE_SIZE));
  const currentMemberPage = Math.min(memberPage, memberPageCount - 1);
  const pagedMembers = members.slice(currentMemberPage * MEMBERS_PAGE_SIZE, (currentMemberPage + 1) * MEMBERS_PAGE_SIZE);
  // Mirror the server's last-admin guard: count only ACTIVE admins (a banned admin never reaches the
  // directory, so this is the active non-banned admin count). When there is exactly one, that admin's
  // Ban action is disabled — the server would reject it with `last_admin` anyway.
  const activeAdminCount = useMemo(
    () => members.filter((m) => m.role === "admin" && m.status === "active").length,
    [members],
  );

  const columns = useMemo<readonly DataColumn<TeamMember>[]>(
    () => [
      {
        key: "email",
        header: "Member",
        isRowHeader: true,
        cell: (m) => (
          <span className="oa-account__member">
            <span className="oa-account__member-name">
              {m.displayName !== undefined && m.displayName.length > 0 ? m.displayName : m.email}
            </span>
            {m.displayName !== undefined && m.displayName.length > 0 && (
              <span className="oa-account__member-email">{m.email}</span>
            )}
          </span>
        ),
      },
      {
        key: "role",
        header: "Role",
        cell: (m) => (
          <StatusPill tone={m.role === "admin" ? "accent" : "neutral"}>{m.role}</StatusPill>
        ),
      },
      {
        key: "status",
        header: "Status",
        cell: (m) => (
          <StatusPill tone={m.status === "active" ? "safe" : m.status === "invited" ? "warn" : "muted"}>
            {m.status}
          </StatusPill>
        ),
      },
    ],
    [],
  );

  return (
    <section className="oa-auth oa-account" aria-label="Member Management">
      <header className="oa-account__header oa-account__header--row">
        <div>
          <h1 className="oa-account__title">Member Management</h1>
          <p className="oa-account__subtitle">
            Manage who can access <strong>{props.teamName}</strong> and what they can do.
          </p>
        </div>
        <button
          type="button"
          className="oa-account__btn"
          onClick={() => {
            setBanner(null);
            setInviteOpen(true);
          }}
        >
          Invite member
        </button>
      </header>

      {banner !== null && (
        <p className="oa-account__feedback" data-kind="ok" role="status" aria-live="polite">
          {banner}
        </p>
      )}

      {banError !== null && (
        <p className="oa-account__feedback" data-kind="error" role="alert">
          {banError}
        </p>
      )}

      {/* Pending SSO access requests — issue 2: moved to the VERY TOP, before the members panel. */}
      <AccessRequestsCard selfId={props.selfId} />

      {/* Members on the left, the team's license key card on the right (license-key-cards 14.3). The
          card is inside this admin-only view and has its own loading/error states, independent of
          `membersQuery`. It stacks under the table at <= 760px (see account.css). */}
      <div className="oa-account__members-row">
        <div className="oa-account__card oa-account__members-panel" aria-labelledby="oa-members-title">
          <h2 className="oa-account__card-title" id="oa-members-title">Team members</h2>
          {membersQuery.isPending ? (
            <div className="oa-account__state" role="status" aria-live="polite">
              Loading members…
            </div>
          ) : membersQuery.isError ? (
            <div className="oa-account__feedback" data-kind="error" role="alert">
              {membersErrorMessage(membersQuery.error)}
            </div>
          ) : (
            <>
              {/* The panel is as tall as the license card beside it: the table scrolls inside it. */}
              <div className="oa-account__members-scroll">
                <DataTable
                  caption="Team members and their roles"
                  captionHidden
                  columns={columns}
                  rows={pagedMembers}
                  rowKey={(m) => m.id}
                  emptyMessage="No members yet. Invite someone to get started."
                  stickyHeader
                  onRowActivate={(m) => {
                    setBanner(null);
                    setSelected(m);
                  }}
                  rowLabel={(m) => `Manage ${m.email}`}
                />
              </div>
              {members.length > MEMBERS_PAGE_SIZE && (
                <nav className="oa-account__pager" aria-label="Team members pages">
                  <span className="oa-account__pager-info" aria-live="polite">
                    Page {currentMemberPage + 1} of {memberPageCount}
                  </span>
                  <button
                    type="button"
                    className="oa-account__btn oa-account__btn--ghost"
                    disabled={currentMemberPage === 0}
                    onClick={() => setMemberPage(currentMemberPage - 1)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="oa-account__btn oa-account__btn--ghost"
                    disabled={currentMemberPage >= memberPageCount - 1}
                    onClick={() => setMemberPage(currentMemberPage + 1)}
                  >
                    Next
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
        {/* No visible title: the card's own PRODUCTION badge says what it is. The accessible name stays. */}
        <LicenseCard scope="tenant" hideTitle />
      </div>

      {/* Single sign-on (OIDC) for this team (admin area) — issue 6: SSO config first. */}
      <SsoCard />

      {/* Organization registration then Verification (admin area) — issue 6 order handled inside
          OrgSettings: registration BELOW SSO, verification BELOW registration. */}
      <OrgSettings canEdit={true} />

      {/* Banned members of this team, with an Unban action (admin area). */}
      <BannedUsersCard />

      <ConfirmBanDialog
        member={confirmBan}
        busy={banMutation.isPending}
        onCancel={() => setConfirmBan(null)}
        onConfirm={() => {
          if (confirmBan !== null) banMutation.mutate(confirmBan.id);
        }}
      />

      <AddUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={async (email) => {
          setInviteOpen(false);
          await refresh();
          setBanner(`Invite sent to ${email}.`);
        }}
      />

      <UserAccessDrawer
        member={selected}
        selfId={props.selfId}
        activeAdminCount={activeAdminCount}
        onClose={() => setSelected(null)}
        onChanged={async (message) => {
          setSelected(null);
          await refresh();
          setBanner(message);
        }}
        onRequestBan={(m) => {
          // Close the drawer and open the confirm dialog (a ban is an access-control change).
          setSelected(null);
          setBanner(null);
          setBanError(null);
          setConfirmBan(m);
        }}
      />
    </section>
  );
}

// -------------------------------------------------------------------------------------------------
// AddUserModal — invite an operator
// -------------------------------------------------------------------------------------------------

function AddUserModal(props: {
  open: boolean;
  onClose: () => void;
  onInvited: (email: string) => void | Promise<void>;
}): ReactNode {
  const emailId = useId();
  const roleId = useId();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("viewer");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: { email: string; role: MemberRole }) => inviteMember(input),
    onSuccess: (_data, input) => {
      setEmail("");
      setRole("viewer");
      setError(null);
      void props.onInvited(input.email);
    },
    onError: (e) => setError(friendlyError(e, "Could not send the invite.")),
  });

  const submit = useCallback(() => {
    setError(null);
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setError("Enter an email address to invite.");
      return;
    }
    mutation.mutate({ email: trimmed, role });
  }, [email, role, mutation]);

  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      title="Invite a member"
      footer={
        <>
          <button type="button" className="oa-account__btn oa-account__btn--ghost" onClick={props.onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="oa-account__btn"
            disabled={mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending ? "Sending…" : "Send invite"}
          </button>
        </>
      }
    >
      <div className="oa-account__row">
        <label className="oa-account__label" htmlFor={emailId}>
          Email
        </label>
        <input
          id={emailId}
          className="oa-account__input"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="oa-account__row">
        <label className="oa-account__label" htmlFor={roleId}>
          Role
        </label>
        <select
          id={roleId}
          className="oa-account__select"
          value={role}
          onChange={(e) => setRole(e.target.value as MemberRole)}
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      {error !== null && (
        <p className="oa-account__feedback" data-kind="error" role="alert">
          {error}
        </p>
      )}
    </Drawer>
  );
}

// -------------------------------------------------------------------------------------------------
// UserAccessDrawer — change role / remove a member
// -------------------------------------------------------------------------------------------------

function UserAccessDrawer(props: {
  member: TeamMember | null;
  selfId: string | null;
  activeAdminCount: number;
  onClose: () => void;
  onChanged: (message: string) => void | Promise<void>;
  onRequestBan: (member: TeamMember) => void;
}): ReactNode {
  const member = props.member;
  const roleId = useId();
  const [role, setRole] = useState<MemberRole>("viewer");
  const [error, setError] = useState<string | null>(null);
  // Track the member id the local `role` state was seeded from, so opening a different member resets
  // the select without a layout effect.
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (member !== null && seededFor !== member.id) {
    setSeededFor(member.id);
    setRole(member.role);
    setError(null);
  }

  const roleMutation = useMutation({
    mutationFn: (next: { id: string; role: MemberRole }) => changeMemberRole(next.id, next.role),
    onSuccess: () => void props.onChanged("Role updated."),
    onError: (e) => setError(friendlyError(e, "Could not update the role.")),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => removeMember(id),
    onSuccess: () => void props.onChanged("Member removed."),
    onError: (e) => setError(friendlyError(e, "Could not remove the member.")),
  });

  const busy = roleMutation.isPending || removeMutation.isPending;

  const displayName =
    member !== null && member.displayName !== undefined && member.displayName.length > 0
      ? member.displayName
      : (member?.email ?? "");

  return (
    <Drawer
      open={member !== null}
      onClose={props.onClose}
      title="Member access"
      footer={
        member === null ? null : (
          <>
            {/* Ban is hidden on the caller's own row (self-ban is refused server-side) and disabled
                for the last active admin (so a team is never left with no admin), mirroring the
                server guards. */}
            {(props.selfId === null || member.id !== props.selfId) && (
              <button
                type="button"
                className="oa-account__btn oa-account__btn--danger"
                aria-label={`Ban ${member.email}`}
                disabled={
                  busy ||
                  (member.role === "admin" && member.status === "active" && props.activeAdminCount <= 1)
                }
                title={
                  member.role === "admin" && member.status === "active" && props.activeAdminCount <= 1
                    ? "Cannot ban the last active admin"
                    : undefined
                }
                onClick={() => props.onRequestBan(member)}
              >
                Ban
              </button>
            )}
            <button
              type="button"
              className="oa-account__btn oa-account__btn--danger"
              disabled={busy}
              onClick={() => removeMutation.mutate(member.id)}
            >
              {removeMutation.isPending ? "Removing…" : "Remove from team"}
            </button>
            <button
              type="button"
              className="oa-account__btn"
              disabled={busy || role === member.role}
              onClick={() => roleMutation.mutate({ id: member.id, role })}
            >
              {roleMutation.isPending ? "Saving…" : "Save role"}
            </button>
          </>
        )
      }
    >
      {member !== null && (
        <div className="oa-account__drawer-body">
          <dl className="oa-account__facts">
            <div className="oa-account__fact">
              <dt>Name</dt>
              <dd>{displayName}</dd>
            </div>
            <div className="oa-account__fact">
              <dt>Email</dt>
              <dd>{member.email}</dd>
            </div>
            <div className="oa-account__fact">
              <dt>Status</dt>
              <dd>
                <StatusPill tone={member.status === "active" ? "safe" : "warn"}>
                  {member.status}
                </StatusPill>
              </dd>
            </div>
          </dl>
          <div className="oa-account__row">
            <label className="oa-account__label" htmlFor={roleId}>
              Role
            </label>
            <select
              id={roleId}
              className="oa-account__select"
              value={role}
              disabled={busy}
              onChange={(e) => setRole(e.target.value as MemberRole)}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          {error !== null && (
            <p className="oa-account__feedback" data-kind="error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </Drawer>
  );
}

// -------------------------------------------------------------------------------------------------
// SsoCard — per-team OIDC single sign-on configuration (admin area)
// -------------------------------------------------------------------------------------------------

const SSO_PROVIDERS: ReadonlyArray<{ readonly id: SsoProviderId; readonly label: string; readonly logo: string }> = [
  // Logos are placeholder PNGs under /sso/*.png (public/sso); replace the files with real logos
  // later (same names). `custom` is the "not listed" tile where an admin names it + uploads a logo.
  { id: "entra", label: "Microsoft Entra ID", logo: "/sso/entra.png" },
  { id: "okta", label: "Okta", logo: "/sso/okta.png" },
  { id: "1kosmos", label: "1Kosmos", logo: "/sso/1kosmos.png" },
  { id: "google", label: "Google", logo: "/sso/google.png" },
  { id: "other", label: "Custom", logo: "/sso/custom.png" },
];

/**
 * The team's single sign-on (OIDC) configuration. A team admin selects their IdP, provides the
 * issuer URL, client id, client secret, redirect URI and scopes, and can turn on enforcement (which
 * requires the team's members to sign in through the IdP). One provider per team. The client secret
 * is write-only: the server never returns it, so an admin editing an existing config leaves the
 * secret field blank to keep the stored one. The authorization/token/JWKS endpoints are discovered
 * from the issuer on save.
 */
function SsoCard(): ReactNode {
  const queryClient = useQueryClient();
  const providerId = useId();
  const labelId = useId();
  const logoId = useId();
  const issuerId = useId();
  const clientIdId = useId();
  const secretId = useId();
  const redirectId = useId();
  const scopeId = useId();

  const query = useQuery({ queryKey: TEAM_SSO_KEY, queryFn: ({ signal }) => getTeamSso(signal), staleTime: 15_000, retry: 1 });

  const defaultRedirect = typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : "";

  const [provider, setProvider] = useState<SsoProviderId>("entra");
  const [label, setLabel] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [redirectUri, setRedirectUri] = useState(defaultRedirect);
  const [scope, setScope] = useState("openid email profile");
  const [enforced, setEnforced] = useState(false);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Issue 3: an admin must TEST the OIDC connection before the save/enforce/disable controls appear.
  // A team that is already configured is treated as already-validated (its endpoints were discovered
  // on the prior save), so its controls show without re-testing — until the issuer is edited.
  const [testResult, setTestResult] = useState<TeamSsoTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  // Seed the form from the loaded config once (or reset to defaults when there is none).
  const data: TeamSso | null | undefined = query.data;
  const seedKey = data === undefined ? "pending" : data === null ? "none" : data.issuer;
  if (query.isSuccess && seededFor !== seedKey) {
    setSeededFor(seedKey);
    if (data != null) {
      const cfg: TeamSso = data;
      setProvider(cfg.providerId);
      setLabel(cfg.providerLabel);
      setLogoUrl(cfg.providerLogoUrl ?? "");
      setIssuer(cfg.issuer);
      setClientId(cfg.clientId);
      setClientSecret("");
      setRedirectUri(cfg.redirectUri || defaultRedirect);
      setScope(cfg.scope || "openid email profile");
      setEnforced(cfg.enforced);
    }
  }

  const configured = data !== null && data !== undefined;

  const saveMutation = useMutation({
    mutationFn: () =>
      saveTeamSso({
        providerId: provider,
        providerLabel: label.trim() || (SSO_PROVIDERS.find((p) => p.id === provider)?.label ?? provider),
        ...(logoUrl.trim() !== "" ? { providerLogoUrl: logoUrl.trim() } : {}),
        issuer: issuer.trim(),
        clientId: clientId.trim(),
        ...(clientSecret !== "" ? { clientSecret } : {}),
        redirectUri: redirectUri.trim(),
        scope: scope.trim(),
        enforced,
      }),
    onSuccess: async () => {
      setError(null);
      setClientSecret("");
      setFeedback("SSO configuration saved.");
      await queryClient.invalidateQueries({ queryKey: TEAM_SSO_KEY });
    },
    onError: (e) => {
      setFeedback(null);
      setError(e instanceof ApiError ? e.message : "Could not save the SSO configuration.");
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => disableTeamSso(),
    onSuccess: async () => {
      setError(null);
      setFeedback("SSO disabled for this team.");
      setSeededFor(null);
      setProvider("entra"); setLabel(""); setLogoUrl(""); setIssuer(""); setClientId("");
      setClientSecret(""); setRedirectUri(defaultRedirect); setScope("openid email profile"); setEnforced(false);
      await queryClient.invalidateQueries({ queryKey: TEAM_SSO_KEY });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not disable SSO."),
  });

  const busy = saveMutation.isPending || disableMutation.isPending;
  const submit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFeedback(null);
      setError(null);
      if (issuer.trim() === "" || clientId.trim() === "" || redirectUri.trim() === "") {
        setError("Issuer, client id and redirect URI are required.");
        return;
      }
      if (!configured && clientSecret === "") {
        setError("A client secret is required.");
        return;
      }
      saveMutation.mutate();
    },
    [issuer, clientId, redirectUri, clientSecret, configured, saveMutation],
  );

  // A team already configured is validated by its prior save; a new/edited config is validated only
  // once the Test SSO Provider check passes (issue 3). Editing the issuer resets testResult to null.
  const validated = configured || (testResult !== null && testResult.ok);

  return (
    <article className="oa-account__card" aria-labelledby="oa-sso-title">
      <h2 className="oa-account__card-title" id="oa-sso-title">
        Single sign-on (SSO)
      </h2>
      <p className="oa-account__card-note">
        Let this team sign in through your identity provider (OIDC). Enter your provider details,
        then optionally enforce SSO so every member must sign in through it. One provider per team.
      </p>

      {query.isError && (
        <p className="oa-account__feedback" data-kind="error" role="alert">
          {friendlyError(query.error, "Could not load the SSO configuration.")}
        </p>
      )}

      <form className="oa-account__form" onSubmit={submit}>
        <div className="oa-account__row">
          <span className="oa-account__label" id={providerId}>Identity provider</span>
          <div className="oa-sso-grid" role="radiogroup" aria-labelledby={providerId}>
            {SSO_PROVIDERS.map((p) => {
              const selected = provider === p.id;
              // The custom tile shows the uploaded logo (if any) once selected; otherwise its placeholder.
              const tileLogo = p.id === "other" && logoUrl !== "" ? logoUrl : p.logo;
              const tileLabel = p.id === "other" && label.trim() !== "" ? label.trim() : p.label;
              return (
                <button
                  type="button"
                  key={p.id}
                  className="oa-sso-tile"
                  data-selected={selected ? "true" : undefined}
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setProvider(p.id);
                    // Switching to a built-in clears any stale custom name/logo so the saved label is
                    // the provider's own (never a leftover "Custom").
                    if (p.id !== "other") {
                      setLabel("");
                      setLogoUrl("");
                    }
                  }}
                >
                  {selected && <span className="oa-sso-tile__badge" aria-hidden="true">✓ Selected</span>}
                  <img className="oa-sso-tile__logo" src={tileLogo} alt="" width={40} height={40} />
                  <span className="oa-sso-tile__name">{tileLabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {provider === "other" && (
          <>
            <div className="oa-account__row">
              <label className="oa-account__label" htmlFor={labelId}>Provider name</label>
              <input id={labelId} className="oa-account__input" type="text" maxLength={64} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Your provider's name" />
            </div>
            <div className="oa-account__row">
              <label className="oa-account__label" htmlFor={logoId}>Provider logo (transparent PNG)</label>
              <input
                id={logoId}
                className="oa-account__input"
                type="file"
                accept="image/png"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file === undefined) return;
                  if (file.size > 64 * 1024) {
                    setError("The logo must be a PNG of 64 KB or less.");
                    e.target.value = "";
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () => {
                    const result = typeof reader.result === "string" ? reader.result : "";
                    if (result.startsWith("data:image/")) {
                      setLogoUrl(result);
                      setError(null);
                    }
                  };
                  reader.readAsDataURL(file);
                }}
              />
              {logoUrl !== "" && (
                <span className="oa-account__field-hint">
                  <img src={logoUrl} alt="" width={28} height={28} style={{ verticalAlign: "middle", marginRight: 6 }} />
                  Logo selected.
                </span>
              )}
            </div>
          </>
        )}

        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={issuerId}>Issuer URL</label>
          <input id={issuerId} className="oa-account__input" type="url" value={issuer} onChange={(e) => { setIssuer(e.target.value); setTestResult(null); }} placeholder="https://login.example.com/…" required />
        </div>
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={clientIdId}>Client ID</label>
          <input id={clientIdId} className="oa-account__input" type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} required />
        </div>
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={secretId}>
            Client secret{configured ? " (leave blank to keep current)" : ""}
          </label>
          <span className="oa-account__input-reveal">
            <input
              id={secretId}
              className="oa-account__input"
              type={showSecret ? "text" : "password"}
              autoComplete="off"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={configured ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : ""}
            />
            {clientSecret !== "" && (
              <button
                type="button"
                className="oa-account__reveal-btn"
                aria-label={showSecret ? "Hide client secret" : "Show client secret"}
                aria-pressed={showSecret}
                title={showSecret ? "Hide" : "Show"}
                onClick={() => setShowSecret((v) => !v)}
              >
                <EyeIcon off={showSecret} />
              </button>
            )}
          </span>
        </div>
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={redirectId}>Redirect URI</label>
          <input id={redirectId} className="oa-account__input" type="url" value={redirectUri} readOnly aria-readonly="true" />
          <span className="oa-account__field-hint">Register this exact URL at your identity provider. It is fixed to this control plane.</span>
        </div>
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={scopeId}>Scopes</label>
          <input id={scopeId} className="oa-account__input" type="text" value={scope} onChange={(e) => setScope(e.target.value)} placeholder="openid email profile" />
          <span className="oa-account__field-hint">Must include <code>openid</code>.</span>
        </div>

        {/* Issue 3: an already-configured team is treated as validated; a NEW/edited config must pass
            the connection test before the enforce checkbox + save/disable controls appear. */}
        {validated && (
          <div className="oa-account__row">
            <label className="oa-account__label" htmlFor="oa-sso-enforce" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input id="oa-sso-enforce" type="checkbox" checked={enforced} onChange={(e) => setEnforced(e.target.checked)} />
              Enforce SSO for all members of this team
            </label>
          </div>
        )}

        {error !== null && (
          <p className="oa-account__feedback" data-kind="error" role="alert">{error}</p>
        )}
        {feedback !== null && (
          <p className="oa-account__feedback" data-kind="ok" role="status" aria-live="polite">{feedback}</p>
        )}
        {testResult !== null && testResult.ok && (
          <p className="oa-account__feedback" data-kind="ok" role="status" aria-live="polite">
            Connection OK. Discovered the authorization, token and JWKS endpoints for this issuer.
          </p>
        )}
        {testResult !== null && !testResult.ok && (
          <p className="oa-account__feedback" data-kind="error" role="alert">{testResult.reason}</p>
        )}

        <div className="oa-account__btn-row">
          {/* Show the TEST button until the connection has passed (or the team is already configured). */}
          {!validated ? (
            <button
              type="button"
              className="oa-account__btn"
              disabled={testing || issuer.trim() === "" || clientId.trim() === ""}
              onClick={() => {
                setFeedback(null); setError(null); setTesting(true);
                void testTeamSso(issuer.trim())
                  .then((r) => setTestResult(r))
                  .catch((e) => setTestResult({ ok: false, reason: e instanceof ApiError ? e.message : "The SSO provider test failed." }))
                  .finally(() => setTesting(false));
              }}
            >
              {testing ? "Testing…" : "Test SSO Provider"}
            </button>
          ) : (
            <>
              <button type="submit" className="oa-account__btn" disabled={busy}>
                {saveMutation.isPending ? "Saving…" : configured ? "Save changes" : "Enable SSO"}
              </button>
              {configured && (
                <button type="button" className="oa-account__btn oa-account__btn--danger" disabled={busy} onClick={() => disableMutation.mutate()}>
                  {disableMutation.isPending ? "Disabling…" : "Disable SSO"}
                </button>
              )}
            </>
          )}
        </div>
      </form>
    </article>
  );
}

// -------------------------------------------------------------------------------------------------
// AccessRequestsCard — approve / deny self-provisioned SSO users (admin area)
// -------------------------------------------------------------------------------------------------

/**
 * Pending SSO access requests. When a user signs in through the team's SSO for the first time and
 * has no invite, they are self-provisioned into a `pending_approval` membership with no access. A
 * team admin approves (assigning a role, default viewer) or denies (removes the pending membership).
 * The card hides itself when there are no pending requests.
 */
function AccessRequestsCard(props: { selfId: string | null }): ReactNode {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [roleFor, setRoleFor] = useState<Record<string, MemberRole>>({});

  const query = useQuery({
    queryKey: ACCESS_REQUESTS_KEY,
    queryFn: ({ signal }) => listAccessRequests(signal),
    staleTime: 10_000,
    retry: 1,
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ACCESS_REQUESTS_KEY }),
    [queryClient],
  );

  const approve = useMutation({
    mutationFn: (v: { userId: string; role: MemberRole }) => approveAccessRequest(v.userId, v.role),
    onSuccess: async () => { setError(null); await refresh(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not approve the request."),
  });
  const deny = useMutation({
    mutationFn: (userId: string) => denyAccessRequest(userId),
    onSuccess: async () => { setError(null); await refresh(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not deny the request."),
  });
  // Banning a pending request moves it out of Access Requests and into the Banned users list, so
  // invalidate both keys after a successful ban.
  const ban = useMutation({
    mutationFn: (userId: string) => banMember(userId),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        refresh(),
        queryClient.invalidateQueries({ queryKey: BANNED_MEMBERS_KEY }),
      ]);
    },
    onError: (e) => setError(banErrorMessage(e, "Could not ban the request.")),
  });

  const requests: readonly AccessRequest[] = query.data ?? [];
  const busy = approve.isPending || deny.isPending || ban.isPending;

  // Hide the card entirely when there is nothing pending (keeps the page clean for most teams).
  if (query.isSuccess && requests.length === 0 && error === null) return null;

  return (
    <article className="oa-account__card" aria-labelledby="oa-access-req">
      <h2 className="oa-account__card-title" id="oa-access-req">
        Access requests
      </h2>
      <p className="oa-account__card-note">
        People who signed in through single sign-on and are waiting for your approval. Approve to
        grant them access with a role, or deny to remove the request.
      </p>

      {error !== null && (
        <p className="oa-account__feedback" data-kind="error" role="alert">{error}</p>
      )}

      {query.isPending ? (
        <div className="oa-account__state" role="status" aria-live="polite">Loading requests…</div>
      ) : requests.length === 0 ? (
        <p className="oa-account__muted">No pending access requests.</p>
      ) : (
        <ul className="oa-account__passkeys" aria-label="Pending access requests">
          {requests.map((r) => {
            const role = roleFor[r.userId] ?? "viewer";
            return (
              <li key={r.userId} className="oa-account__passkey">
                <span className="oa-account__passkey-meta">
                  <span className="oa-account__passkey-name">{r.displayName ?? r.email}</span>
                  <span className="oa-account__passkey-sub">{r.email}</span>
                </span>
                <span className="oa-account__passkey-actions">
                  <select
                    className="oa-account__select"
                    value={role}
                    disabled={busy}
                    aria-label={`Role for ${r.email}`}
                    onChange={(e) => setRoleFor((prev) => ({ ...prev, [r.userId]: e.target.value as MemberRole }))}
                    style={{ padding: "4px 8px", fontSize: 12 }}
                  >
                    <option value="viewer">viewer</option>
                    <option value="operator">operator</option>
                    <option value="admin">admin</option>
                  </select>
                  <button
                    type="button"
                    className="oa-account__btn"
                    disabled={busy}
                    onClick={() => approve.mutate({ userId: r.userId, role })}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="oa-account__btn oa-account__btn--ghost"
                    disabled={busy}
                    onClick={() => deny.mutate(r.userId)}
                  >
                    Deny
                  </button>
                  {(props.selfId === null || r.userId !== props.selfId) && (
                    <button
                      type="button"
                      className="oa-account__btn oa-account__btn--danger"
                      aria-label={`Ban ${r.email}`}
                      disabled={busy}
                      onClick={() => ban.mutate(r.userId)}
                    >
                      Ban
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

// -------------------------------------------------------------------------------------------------
// ConfirmBanDialog — explicit confirm for a ban (an access-control change)
// -------------------------------------------------------------------------------------------------

/**
 * An accessible confirmation for banning a member. A ban is an access-control change (it revokes the
 * member's access to the team until an admin unbans and re-approves), so it must not be a one-click
 * action — the admin confirms in an `alertdialog`. Rendering nothing when `member` is null keeps the
 * dialog controlled by the parent.
 */
function ConfirmBanDialog(props: {
  member: TeamMember | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactNode {
  const member = props.member;
  const who =
    member === null
      ? ""
      : member.displayName !== undefined && member.displayName.length > 0
        ? `${member.displayName} (${member.email})`
        : member.email;

  return (
    <Modal
      open={member !== null}
      onClose={props.onCancel}
      role="alertdialog"
      title="Ban member"
      description={
        member === null
          ? undefined
          : `Ban ${who} from this team? They lose access immediately. You can unban them later, but access must be approved again.`
      }
      footer={
        <>
          <button
            type="button"
            className="oa-account__btn oa-account__btn--ghost"
            disabled={props.busy}
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="oa-account__btn oa-account__btn--danger"
            disabled={props.busy}
            onClick={props.onConfirm}
          >
            {props.busy ? "Banning…" : "Ban member"}
          </button>
        </>
      }
    >
      <p className="oa-account__card-note">
        This revokes the member's access to this team. Their account and sign-in history are kept; the
        ban only applies to this team.
      </p>
    </Modal>
  );
}

// -------------------------------------------------------------------------------------------------
// BannedUsersCard — list banned members, with an Unban action (admin area)
// -------------------------------------------------------------------------------------------------

/** Format an epoch-ms instant for display, or a dash when absent. */
function formatBannedAt(ms: number | null): string {
  if (ms === null) return "—";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/**
 * The team's banned members. A banned `(user, team)` row grants no access and never counts as an
 * admin; the row is kept so an admin can see who is banned (and by whom) and unban them. Unbanning
 * clears the ban and returns the row to a pending request, so the user re-appears under Access
 * Requests and must be approved again — hence after an unban we invalidate the banned list, the
 * members directory and {@link ACCESS_REQUESTS_KEY}. The card hides itself when no one is banned.
 */
function BannedUsersCard(): ReactNode {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: BANNED_MEMBERS_KEY,
    queryFn: ({ signal }) => listBannedMembers(signal),
    staleTime: 10_000,
    retry: 1,
  });

  const unban = useMutation({
    mutationFn: (userId: string) => unbanMember(userId),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: BANNED_MEMBERS_KEY }),
        queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_KEY }),
        queryClient.invalidateQueries({ queryKey: ACCESS_REQUESTS_KEY }),
      ]);
    },
    onError: (e) => setError(banErrorMessage(e, "Could not unban the member.")),
  });

  const banned: readonly BannedMember[] = query.data ?? [];
  const busy = unban.isPending;

  // Hide the card entirely when no one is banned (keeps the page clean for most teams).
  if (query.isSuccess && banned.length === 0 && error === null) return null;

  return (
    <article className="oa-account__card" aria-labelledby="oa-banned-users">
      <h2 className="oa-account__card-title" id="oa-banned-users">
        Banned users
      </h2>
      <p className="oa-account__card-note">
        Members whose access to this team is revoked. Unban to return them to Access Requests; access
        must be approved again.
      </p>

      {error !== null && (
        <p className="oa-account__feedback" data-kind="error" role="alert">{error}</p>
      )}

      {query.isPending ? (
        <div className="oa-account__state" role="status" aria-live="polite">Loading banned users…</div>
      ) : banned.length === 0 ? (
        <p className="oa-account__muted">No banned users.</p>
      ) : (
        <ul className="oa-account__passkeys" aria-label="Banned users">
          {banned.map((b) => (
            <li key={b.userId} className="oa-account__passkey">
              <span className="oa-account__passkey-meta">
                <span className="oa-account__passkey-name">{b.displayName ?? b.email}</span>
                <span className="oa-account__passkey-sub">{b.email}</span>
                <span className="oa-account__passkey-sub">
                  Banned {formatBannedAt(b.bannedAt)}
                  {b.bannedBy !== null ? ` by ${b.bannedBy}` : ""}
                </span>
              </span>
              <span className="oa-account__passkey-actions">
                <button
                  type="button"
                  className="oa-account__btn"
                  aria-label={`Unban ${b.email}`}
                  disabled={busy}
                  onClick={() => unban.mutate(b.userId)}
                >
                  {unban.isPending ? "Unbanning…" : "Unban"}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
